import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const METRIC_COLORS = {
  total_streams: '#2563eb',
  audio_streams: '#16a34a',
  video_streams: '#dc2626',
  ad_supported_streams: '#9333ea',
  premium_streams: '#ea580c',
  on_demand_streams: '#0d9488',
  programmed_streams: '#4f46e5'
};

const METRIC_LABELS = {
  total_streams: 'Total Streams',
  audio_streams: 'Audio Streams',
  video_streams: 'Video Streams',
  ad_supported_streams: 'Ad-Supported',
  premium_streams: 'Premium',
  on_demand_streams: 'On-Demand',
  programmed_streams: 'Programmed'
};

// Helper function to get day of week from date string
function getDayOfWeek(dateStr) {
  // Handle weekly format "Week ending YYYY-MM-DD"
  if (dateStr.startsWith('Week ending')) {
    return ''; // Don't show day of week for weekly data
  }
  
  const date = new Date(dateStr);
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  return days[date.getDay()];
}

// Helper function to format date for display
function formatDateLabel(dateStr) {
  // Handle weekly format "Week ending YYYY-MM-DD"
  if (dateStr.startsWith('Week ending')) {
    return dateStr;
  }
  
  const dayOfWeek = getDayOfWeek(dateStr);
  return dayOfWeek ? `${dateStr} (${dayOfWeek})` : dateStr;
}

export default function MetricsChart({ dates, metrics, selectedMetrics = ['audio'], percentChanges }) {
  // Add null check for metrics
  if (!metrics || !dates) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading chart data...</div>
      </div>
    );
  }

  // Map the short keys to the actual metric keys in the data
  const metricKeyMap = {
    total: 'total_streams',
    audio: 'audio_streams',
    video: 'video_streams',
    ad: 'ad_supported_streams',
    premium: 'premium_streams',
    ondemand: 'on_demand_streams',
    programmed: 'programmed_streams'
  };

  // Filter metrics based on selection using the mapped keys
  const visibleMetrics = Object.entries(metrics)
    .filter(([key]) => selectedMetrics.includes(Object.entries(metricKeyMap).find(([_, v]) => v === key)?.[0]))
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  // Trim trailing all-zero data points: find the last index where any visible
  // series has a non-zero value, and cut everything after it. Recent dates with
  // no reported streams (data lag) would otherwise dip the line to 0 at the tail.
  const visibleSeries = Object.values(visibleMetrics);
  let lastNonZero = dates.length - 1;
  if (visibleSeries.length) {
    lastNonZero = -1;
    for (let i = dates.length - 1; i >= 0; i--) {
      if (visibleSeries.some(values => Number(values?.[i]) > 0)) {
        lastNonZero = i;
        break;
      }
    }
  }
  const trimLen = lastNonZero + 1;
  const trimmedDates = dates.slice(0, trimLen);

  const data = {
    labels: trimmedDates,
    datasets: Object.entries(visibleMetrics).map(([key, values]) => ({
      label: METRIC_LABELS[key],
      data: values.slice(0, trimLen),
      borderColor: METRIC_COLORS[key],
      backgroundColor: METRIC_COLORS[key],
      tension: 0.4,
      pointRadius: 2,
      pointHoverRadius: 6, // Make hover dots bigger
      pointHoverBackgroundColor: METRIC_COLORS[key],
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
    }))
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: function(tooltipItems) {
            // Format the date with day of week in the tooltip title
            if (tooltipItems.length > 0) {
              const dateStr = tooltipItems[0].label;
              return formatDateLabel(dateStr);
            }
            return '';
          },
          label: function(context) {
            // Format the value with commas
            const value = context.parsed.y;
            const formattedValue = value.toLocaleString();
            
            // Get the metric key from the dataset
            const datasetLabel = context.dataset.label;
            const metricKey = Object.entries(METRIC_LABELS).find(([_, label]) => label === datasetLabel)?.[0];
            
            // Check if we have percent changes and this is weekly data
            if (percentChanges && dates[0]?.startsWith('Week ending') && metricKey) {
              const percentChangeKey = `${metricKey}_percent_change`;
              const percentChange = percentChanges[percentChangeKey]?.[context.dataIndex];
              
              if (percentChange !== null && percentChange !== undefined) {
                // Format percent change with sign
                const sign = percentChange >= 0 ? '+' : '';
                const formattedPercent = `${sign}${percentChange.toFixed(1)}%`;
                return `${context.dataset.label}: ${formattedValue} (${formattedPercent})`;
              }
            }
            
            return `${context.dataset.label}: ${formattedValue}`;
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        bodySpacing: 8,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: '#E2E8F0'
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      y: {
        grid: {
          display: true,
          color: '#E2E8F0'
        },
        ticks: {
          font: {
            size: 12
          },
          callback: function(value) {
            // Format y-axis values with commas
            return value.toLocaleString();
          }
        }
      }
    }
  };

  return <Line data={data} options={options} />;
} 